import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../utils/api';
import { Button } from '../components/ui/button';
import { X, Crown, Zap, ArrowRight } from 'lucide-react';

const PlanContext = createContext(null);

export const usePlan = () => useContext(PlanContext);

export const PlanProvider = ({ children }) => {
  const [plan, setPlan] = useState(null);
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [upgradeReason, setUpgradeReason] = useState('');
  const [upgradeTo, setUpgradeTo] = useState('starter');

  const fetchPlan = useCallback(async () => {
    try {
      const res = await apiClient.get('/plan/current');
      setPlan(res.data.plan);
      setUsage(res.data.usage);
      setLimits(res.data.limits);
    } catch {}
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  const checkFeature = useCallback(async (feature) => {
    try {
      const res = await apiClient.post('/plan/check-feature', { feature });
      if (!res.data.allowed) {
        setUpgradeReason(res.data.reason);
        setUpgradeTo(res.data.upgrade_to || 'starter');
        setShowUpgrade(true);
        return false;
      }
      return true;
    } catch (err) {
      if (err.response?.status === 403) {
        const detail = err.response.data?.detail;
        if (detail?.reason) {
          setUpgradeReason(detail.reason);
          setUpgradeTo(detail.upgrade_to || 'starter');
          setShowUpgrade(true);
        }
        return false;
      }
      return true;
    }
  }, []);

  const handleApiError = useCallback((err) => {
    if (err.response?.status === 403) {
      const detail = err.response.data?.detail;
      if (detail && typeof detail === 'object' && detail.reason) {
        setUpgradeReason(detail.reason);
        setUpgradeTo(detail.upgrade_to || 'starter');
        setShowUpgrade(true);
        return true;
      }
    }
    return false;
  }, []);

  // Manual trigger for client-side gates (e.g., locked premium templates)
  const promptUpgrade = useCallback((reason, upgradeTo = 'pro') => {
    setUpgradeReason(reason);
    setUpgradeTo(upgradeTo);
    setShowUpgrade(true);
  }, []);

  return (
    <PlanContext.Provider value={{ plan, usage, limits, fetchPlan, checkFeature, handleApiError, promptUpgrade }}>
      {children}
      {showUpgrade && (
        <UpgradeModal
          reason={upgradeReason}
          upgradeTo={upgradeTo}
          onClose={() => setShowUpgrade(false)}
        />
      )}
    </PlanContext.Provider>
  );
};

const PLAN_INFO = {
  starter: { name: 'Starter', price: '$19/mo', color: '#3B82F6', icon: Zap },
  pro: { name: 'Pro', price: '$39/mo', color: '#8B5CF6', icon: Crown },
  team: { name: 'Team', price: '$149/mo', color: '#10B981', icon: Crown },
};

const UpgradeModal = ({ reason, upgradeTo, onClose }) => {
  const navigate = useNavigate();
  const info = PLAN_INFO[upgradeTo] || PLAN_INFO.starter;
  const Icon = info.icon;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="upgrade-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center relative animate-in fade-in zoom-in-95 duration-200">
        <button onClick={onClose} className="absolute top-4 right-4 p-1 rounded-lg hover:bg-gray-100" data-testid="upgrade-modal-close">
          <X className="h-5 w-5 text-gray-400" />
        </button>

        <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${info.color}15` }}>
          <Icon className="h-8 w-8" style={{ color: info.color }} />
        </div>

        <h2 className="text-xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          Upgrade to unlock
        </h2>

        <p className="text-sm text-gray-500 mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {reason}
        </p>

        <Button
          className="w-full h-11 rounded-xl text-white mb-3"
          style={{ background: `linear-gradient(135deg, ${info.color}, ${info.color}cc)` }}
          onClick={() => { onClose(); navigate('/subscription'); }}
          data-testid="upgrade-modal-btn"
        >
          <ArrowRight className="h-4 w-4 mr-2" />
          Upgrade to {info.name} — {info.price}
        </Button>

        <button onClick={onClose} className="text-sm text-gray-400 hover:text-gray-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Maybe later
        </button>
      </div>
    </div>
  );
};

export default PlanProvider;
